import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

/* ─────────────────────────── Skin ─────────────────────────── */

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

/* ─────────────────────────── Formatters ─────────────────────────── */

function formatValue(value, fallback = "") {
  return value === null || value === undefined || value === ""
    ? fallback
    : String(value);
}

/**
 * Display a SQL datetime string as IST wall-clock time.
 * Never passes through new Date() to avoid timezone shift.
 * Input:  "2026-05-29 16:55:00" or "2026-05-29T16:55:00" or "2026-05-29T16:55:00.000Z"
 * Output: "29 May 2026, 04:55:00 pm"
 */
function formatDateTime(value, fallback = "") {
  if (!value) return fallback;

  const text = String(value)
    .replace("T", " ")
    .replace("Z", "")
    .split(".")[0]
    .trim();

  const [datePart, timePart] = text.split(" ");
  if (!datePart || !timePart) return text;

  const [year, month, day] = datePart.split("-").map(Number);
  const [hStr, mStr, sStr = "00"] = timePart.split(":");
  const hour = Number(hStr);
  const ampm = hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 || 12;

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}, ${String(
    hour12
  ).padStart(2, "0")}:${mStr}:${sStr} ${ampm}`;
}

/**
 * Convert a stored SQL datetime string to a datetime-local input value.
 * Strips Z and milliseconds so the browser input shows the correct IST time.
 * Input:  "2026-05-29T16:55:00.000Z" or "2026-05-29 16:55:00"
 * Output: "2026-05-29T16:55"
 */
function toDateTimeLocal(value) {
  if (!value) return "";

  return String(value)
    .replace("T", " ")
    .replace("Z", "")
    .split(".")[0]
    .trim()
    .replace(" ", "T")
    .slice(0, 16);
}

/**
 * Parse a datetime-local string for comparison ONLY (no storage).
 * Uses Date.UTC to avoid any timezone offset.
 * Input:  "2026-05-29T16:55" or "2026-05-29T16:55:00"
 * Output: ms number or null
 */
function parseLocalForCompare(str) {
  if (!str) return null;

  const s = String(str).trim().replace("T", " ");
  const [datePart, timePart] = s.split(" ");

  if (!datePart || !timePart) return null;

  const [year, month, day]      = datePart.split("-").map(Number);
  const [hour, minute, sec = 0] = timePart.split(":").map(Number);

  if (
    !Number.isFinite(year)   ||
    !Number.isFinite(month)  ||
    !Number.isFinite(day)    ||
    !Number.isFinite(hour)   ||
    !Number.isFinite(minute)
  ) return null;

  return Date.UTC(year, month - 1, day, hour, minute, sec);
}

/* ─────────────────────────── Constants ─────────────────────────── */

const RECORDS_PER_PAGE = 10;

/* ─────────────────────────── Main component ─────────────────────────── */

export default function RouteAllocations() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const routeId          = Number(params.get("route_id") || 0);
  const initialRouteName = params.get("route_name") || "";
  const initialSearch    = params.get("q") || "";
  const initialPage      = Number(params.get("page") || 1);

  /* ── State ── */
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [deletingId,   setDeletingId]   = useState(null);

  const [dashboardData, setDashboardData] = useState({
    company_name: "", licensedFor: "", admin_active: 0,
    company_address: "", company_phone: "", admin_email: "",
    company_logo: "", user_map: "",
  });

  // Keep a ref so callbacks always see the latest dashboardData
  const dashboardDataRef = useRef(dashboardData);
  useEffect(() => { dashboardDataRef.current = dashboardData; }, [dashboardData]);

  const [routeName,     setRouteName]     = useState(initialRouteName);
  const [searchInput,   setSearchInput]   = useState(initialSearch);
  const [search,        setSearch]        = useState(initialSearch);
  const [page,          setPage]          = useState(initialPage > 0 ? initialPage : 1);

  const [allocations,   setAllocations]   = useState([]);
  const [guards,        setGuards]        = useState([]);
  const [totalRecords,  setTotalRecords]  = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [allRows,       setAllRows]       = useState([]); // full unfiltered list for client search

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    allocation_id:   "",
    guard_member_id: "",
    shift_label:     "",
    start_time:      "",
    end_time:        "",
  });

  /* ── Helpers ── */
  function getToken() {
    return localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  }

  function getLocalDashboard() {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    return {
      company_name:    localStorage.getItem("company_name")    || userData.company_name    || "",
      licensedFor:     localStorage.getItem("licensedFor")     || userData.licensedFor     || "",
      admin_active:    Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone:   localStorage.getItem("company_phone")   || userData.company_phone   || "",
      admin_email:     localStorage.getItem("admin_email")     || userData.admin_email     || "",
      company_logo:    localStorage.getItem("company_logo")    || userData.company_logo    || "",
      user_map:        localStorage.getItem("user_map")        || userData.user_map        || "",
    };
  }

  /* ── Fetch guards ── */
  const fetchGuards = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        cache: "no-store",
      });

      const data = await res.json();
      setGuards(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Fetch guards error:", err);
      setGuards([]);
    }
  }, []);

  /* ── Fetch allocations (full list from API, then client-side paginate) ── */
  const fetchAllocations = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/route-allocation/${encodeURIComponent(routeId)}`,
        {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          cache: "no-store",
        }
      );

      if (!res.ok) throw new Error("Failed to fetch allocations");

      const data = await res.json();
      return Array.isArray(data?.data) ? data.data : [];
    } catch (err) {
      console.error("Fetch allocations error:", err);
      return [];
    }
  }, [routeId]);

  /* ── Apply search + pagination on the client ── */
  const applySearchAndPage = useCallback(
    (rows, currentSearch, currentPage) => {
      let filtered = rows;

      if (currentSearch?.trim()) {
        const kw = currentSearch.trim().toLowerCase();

        filtered = rows.filter((row) => {
          const guardText = String(
            row.guard_label || row.guard_name || row.username || row.guard_member_id || ""
          ).toLowerCase();
          const shiftText = String(row.shift_label || "").toLowerCase();

          return guardText.includes(kw) || shiftText.includes(kw);
        });
      }

      const total    = filtered.length;
      const pages    = Math.max(1, Math.ceil(total / RECORDS_PER_PAGE));
      const safePage = Math.min(currentPage, pages);
      const start    = (safePage - 1) * RECORDS_PER_PAGE;
      const paged    = filtered.slice(start, start + RECORDS_PER_PAGE);

      setAllocations(paged);
      setTotalRecords(total);
      setTotalPages(pages);
    },
    []
  );

  /* ── Full page load ── */
  const loadPage = useCallback(
    async (currentSearch, currentPage) => {
      setLoading(true);

      try {
        const token = getToken();

        const [dashboardRes, rows] = await Promise.all([
          fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetchAllocations(),
        ]);

        // Dashboard data (non-blocking if it fails)
        if (dashboardRes.ok) {
          const dashboardResult = await dashboardRes.json();
          const cd = dashboardResult?.data?.companyDetails || {};
          const local = dashboardDataRef.current;

          const merged = {
            company_name:    cd.company_name    ?? local.company_name    ?? "",
            licensedFor:     cd.licensedFor     ?? local.licensedFor     ?? "",
            admin_active:    Number(cd.admin_active ?? local.admin_active ?? 0),
            company_address: cd.company_address ?? local.company_address ?? "",
            company_phone:   cd.company_phone   ?? local.company_phone   ?? "",
            admin_email:     cd.admin_email     ?? local.admin_email     ?? "",
            company_logo:    cd.company_logo    ?? local.company_logo    ?? "",
            user_map:        cd.user_map        ?? local.user_map        ?? "",
          };

          setDashboardData(merged);
        }

        setAllRows(rows);
        setRouteName(initialRouteName || `Route #${routeId}`);
        applySearchAndPage(rows, currentSearch, currentPage);
      } catch (err) {
        console.error("loadPage error:", err);
        toast.error(err.message || "Failed to load route allocations.");
        setAllocations([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [fetchAllocations, applySearchAndPage, initialRouteName, routeId]
  );

  /* ── Initial load ── */
  useEffect(() => {
    const token     = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) { navigate("/"); return; }

    if (!routeId) {
      toast.error("Missing or invalid route ID.");
      setLoading(false);
      return;
    }

    setDashboardData(getLocalDashboard());
    loadPage(search, page);
    fetchGuards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Re-apply search/page when they change (no full API refetch needed) ── */
  useEffect(() => {
    applySearchAndPage(allRows, search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  /* ── URL sync ── */
  const updateUrl = useCallback(
    (nextPage, nextSearch) => {
      const query = new URLSearchParams();
      query.set("route_id", String(routeId));
      if (routeName)              query.set("route_name", routeName);
      if (nextSearch?.trim())     query.set("q",          nextSearch.trim());
      if (nextPage > 1)           query.set("page",       String(nextPage));
      navigate(`/route_allocation?${query.toString()}`);
    },
    [navigate, routeId, routeName]
  );

  /* ── Search handlers ── */
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const nextSearch = searchInput.trim();
    setPage(1);
    setSearch(nextSearch);
    updateUrl(1, nextSearch);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    updateUrl(1, "");
    toast.success("Search cleared");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    updateUrl(nextPage, search);
  };

  /* ── Map button ── */
  const openRouteMap = (allocation) => {
    const url = `/admin_route_tracking?route_id=${encodeURIComponent(
      routeId
    )}&user_id=${encodeURIComponent(
      allocation.guard_member_id
    )}&alloc_id=${encodeURIComponent(allocation.allocation_id)}`;
    window.open(url, "_blank");
  };

  /* ── Edit modal ── */
  const openEditModal = (allocation) => {
    setEditForm({
      allocation_id:   allocation.allocation_id   || "",
      guard_member_id: allocation.guard_member_id || "",
      shift_label:     allocation.shift_label     || "",
      start_time:      toDateTimeLocal(allocation.start_time),
      end_time:        toDateTimeLocal(allocation.end_time),
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm({
      allocation_id: "", guard_member_id: "",
      shift_label: "", start_time: "", end_time: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEditAllocation = async () => {
    if (
      !editForm.allocation_id ||
      !editForm.guard_member_id ||
      !editForm.shift_label.trim() ||
      !editForm.start_time ||
      !editForm.end_time
    ) {
      toast.error("Please complete all fields.");
      return;
    }

    // ── Validate times without new Date() ──
    const startMs = parseLocalForCompare(editForm.start_time);
    const endMs   = parseLocalForCompare(editForm.end_time);

    if (!startMs || !endMs) {
      toast.error("Invalid date/time format.");
      return;
    }

    if (endMs <= startMs) {
      toast.error("End time must be after start time.");
      return;
    }

    try {
      setSaving(true);

      // Send raw datetime-local strings — backend must use toSqlDateTime() / CONVERT
      const res = await fetch(
        `${API_BASE_URL}/route-allocation/${editForm.allocation_id}`,
        {
          method:  "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            guard_member_id: editForm.guard_member_id,
            shift_label:     editForm.shift_label.trim(),
            start_time:      editForm.start_time,  // raw "2026-05-29T16:55" — no new Date()
            end_time:        editForm.end_time,    // raw "2026-05-29T18:55" — no new Date()
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to update allocation");
      }

      toast.success(data.message || "Allocation updated successfully.");
      closeEditModal();

      // Refresh the full list so table shows new values
      const rows = await fetchAllocations();
      setAllRows(rows);
      applySearchAndPage(rows, search, page);
    } catch (err) {
      console.error("Update allocation error:", err);
      toast.error(err.message || "Unexpected error updating allocation.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const deleteAllocation = async (allocationId) => {
    if (!window.confirm("Delete this allocation? This cannot be undone.")) return;

    try {
      setDeletingId(allocationId);

      const res = await fetch(`${API_BASE_URL}/route-allocation/${allocationId}`, {
        method:  "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${getToken()}`,
        },
      });

      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || "Failed to delete");

      toast.success(data.message || "Allocation deleted successfully.");

      // Refresh list after delete
      const rows = await fetchAllocations();
      setAllRows(rows);
      applySearchAndPage(rows, search, page);
    } catch (err) {
      console.error("Delete allocation error:", err);
      toast.error(err.message || "Unexpected error deleting allocation.");
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Pagination items ── */
  const paginationItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end   = Math.min(totalPages, page + 2);

    if (start > 1) { items.push(1); if (start > 2) items.push("..."); }
    for (let i = start; i <= end; i++) items.push(i);
    if (end < totalPages) { if (end < totalPages - 1) items.push("..."); items.push(totalPages); }

    return items;
  }, [page, totalPages]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/create_route" : "/create-route"),
    [dashboardData.user_map]
  );

  /* ─────────────────────────── Render ─────────────────────────── */

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading route allocations...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="routes"
      activeMenu="routes"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
        }}
      />
      
      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Route Allocations
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          Route: <strong>{formatValue(routeName, `Route #${routeId}`)}</strong> • ID: {routeId}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Allocation List
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage route guard allocations, timings, tracking map, edit and delete actions
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/route_list")}
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Back to Routes
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Allocations
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {totalRecords}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Current Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {page} / {totalPages}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Per Page
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {RECORDS_PER_PAGE}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Status
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {allocations.length > 0 ? "Active" : "Empty"}
            </p>
          </div>
        </div>

        {/* Filter Card */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap">
            <input
              type="text"
              placeholder="Search guard or shift..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1 min-w-[260px] rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
            />

            <button
              type="submit"
              className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
              style={{
                background: "var(--btn-bg)",
                color: "var(--btn-text)",
                boxShadow: "0 4px 18px var(--accent-glow)",
              }}
            >
              Search
            </button>

            {search ? (
              <button
                type="button"
                onClick={clearSearch}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
              >
                Clear
              </button>
            ) : null}
          </form>
        </div>

        {/* Allocations Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-5 py-[18px] border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[12px] font-extrabold uppercase tracking-wider text-[var(--text-sub)] m-0">
              Allocations Database
            </p>
            <span className="text-[12px] text-[var(--text-muted)]">
              {totalRecords} entries found
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] border-collapse">
              <thead>
                <tr>
                  {["#", "Guard", "Shift", "Start", "End", "Actions"].map((head) => (
                    <th
                      key={head}
                      className="bg-[var(--surface)] text-[var(--text-sub)] text-left text-[11px] font-extrabold uppercase tracking-wider px-[18px] py-3.5 border-b border-[var(--divider)]"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {allocations.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-8 text-[var(--text-muted)]">
                      {search ? `No allocations match "${search}".` : "No allocations found for this route."}
                    </td>
                  </tr>
                ) : (
                  allocations.map((row, index) => (
                    <tr key={row.allocation_id} className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors">
                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <span className="text-[var(--accent)] font-extrabold">
                          {(page - 1) * RECORDS_PER_PAGE + index + 1}
                        </span>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <div className="flex items-center gap-2.5">
                          <div className="w-[38px] h-[38px] rounded-xl bg-[var(--btn-bg)] text-[var(--btn-text)] flex items-center justify-center text-[14px] font-extrabold flex-shrink-0 shadow-[0_3px_12px_var(--accent-glow)]">
                            {String(row.guard_label || row.guard_name || row.username || "U").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-[13px] font-extrabold text-[var(--text-primary)] m-0">
                              {formatValue(row.guard_label || row.guard_name || row.username, row.guard_member_id)}
                            </p>
                            <p className="text-[12px] text-[var(--text-muted)] m-0">
                              ID: {row.guard_member_id}
                            </p>
                          </div>
                        </div>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[var(--accent-faint)] border border-[var(--accent-border)] text-[var(--accent)]">
                          {formatValue(row.shift_label)}
                        </span>
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                        {formatDateTime(row.start_time, "Not specified")}
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] text-[var(--text-primary)] align-middle">
                        {formatDateTime(row.end_time, "Not specified")}
                      </td>

                      <td className="px-[18px] py-[15px] text-[13px] align-middle">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => openRouteMap(row)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                            style={{
                              background: "#22c55e",
                              color: "white",
                            }}
                          >
                            Map
                          </button>

                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                            style={{
                              background: "#f59e0b",
                              color: "white",
                            }}
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() => deleteAllocation(row.allocation_id)}
                            className="border-0 rounded-xl px-3 py-2 text-[11px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                            style={{
                              background: "var(--red)",
                              color: "white",
                            }}
                            disabled={deletingId === row.allocation_id}
                          >
                            {deletingId === row.allocation_id ? "..." : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="px-5 py-[15px] border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-[var(--text-muted)]">
                Showing {(page - 1) * RECORDS_PER_PAGE + 1} to{" "}
                {Math.min(page * RECORDS_PER_PAGE, totalRecords)} of {totalRecords} entries
              </div>

              <div className="flex gap-1.5 flex-wrap">
                {page > 1 ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page - 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Previous
                  </button>
                ) : null}

                {paginationItems.map((item, index) =>
                  item === "..." ? (
                    <span key={`dots-${index}`} className="text-[var(--text-muted)] text-[16px] font-extrabold px-1 self-center">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => handlePageChange(item)}
                      className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer ${
                        item === page
                          ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                          : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)]"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}

                {page < totalPages ? (
                  <button
                    type="button"
                    onClick={() => handlePageChange(page + 1)}
                    className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer"
                  >
                    Next
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 z-[2500]" onClick={closeEditModal}>
          <div className="w-full max-w-[720px] bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b border-[var(--divider)]">
              <h3 className="m-0 text-[22px] font-extrabold text-[var(--text-primary)]">
                Edit Allocation
              </h3>
              <button
                type="button"
                onClick={closeEditModal}
                className="border-0 bg-transparent text-3xl leading-none cursor-pointer text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-6 bg-[var(--surface)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Guard <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <select
                    name="guard_member_id"
                    value={editForm.guard_member_id}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  >
                    <option value="">
                      {guards.length ? "-- Select Guard --" : "Loading guards..."}
                    </option>
                    {guards.map((guard) => (
                      <option key={guard.member_id} value={guard.member_id}>
                        {guard.username} ({guard.member_id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Shift Label <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    name="shift_label"
                    value={editForm.shift_label}
                    onChange={handleEditChange}
                    placeholder="Morning / Evening / Night / Custom"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Start Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={editForm.start_time}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    End Date & Time <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={editForm.end_time}
                    onChange={handleEditChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-[var(--card-bg)] border-t border-[var(--divider)] flex justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={closeEditModal}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Close
              </button>

              <button
                type="button"
                onClick={saveEditAllocation}
                className="border-0 rounded-xl px-5 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}